// Each press/submit owns its breadcrumbs and timers. Only the report budget is
// shared across the page: another composer or an outbox retry cannot hide a hang.
export type ReplySubmitReportKind = 'tap_lost' | 'blocked' | 'stalled' | 'closed_unsent'
export type CrumbData = Record<string, string | number | boolean | null | undefined>
export type Crumb = { t: number; e: string } & CrumbData
export type ReplySubmitTrace = {
    crumb: (event: string, data?: CrumbData) => void
    startWork: () => void
    apiBegin: () => void
    submitEnd: (outcome: 'handed_off' | 'blocked', data?: CrumbData) => void
    requestSent: (data?: CrumbData) => void
    finish: () => void
}

export const TAP_WINDOW_MS = 2500
export const STALL_WINDOW_MS = 15_000
export const MAX_CRUMBS = 60
export const MAX_REPORTS = 3
// A composer that closes or folds this soon after a touch inside it, with an
// unsent draft and no submit begun, most likely swallowed a post tap.
export const CLOSE_AFTER_TOUCH_MS = 1500
export const RECENT_SUBMIT_MS = 3000
const MAX_RECENT = 20

type Timer = unknown

export function createReplySubmitDiagnostics({
    enabled,
    report,
    listen,
    now = () => (typeof performance === 'undefined' ? Date.now() : performance.now()),
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = timer => clearTimeout(timer as ReturnType<typeof setTimeout>),
}: {
    enabled: () => boolean
    report: (kind: ReplySubmitReportKind, crumbs: Crumb[]) => void
    listen?: (crumb: (event: string, data?: CrumbData) => void) => () => void
    now?: () => number
    setTimer?: (fn: () => void, ms: number) => Timer
    clearTimer?: (timer: Timer) => void
}) {
    let reports = 0
    const safe = (fn: () => void) => {
        try {
            fn()
        } catch {
            // Diagnostic callbacks, including timers and listeners, cannot affect posting.
        }
    }

    return {
        createComposer() {
            const active = new Set<ReplySubmitTrace>()
            let pressed: ReturnType<typeof attempt> | null = null
            // Composer-level breadcrumbs since the last touch inside it, for
            // closes that never reach press/submit.
            let lastTouchAt: number | null = null
            let lastSubmitAt: number | null = null
            let recent: Crumb[] = []
            const remember = (event: string, data: CrumbData = {}) => {
                if (lastTouchAt === null) return
                recent.push({ ...data, t: Math.round(now() - lastTouchAt), e: event })
                if (recent.length > MAX_RECENT) recent = recent.slice(-MAX_RECENT)
            }
            function attempt() {
                let origin = 0
                let closed = true
                safe(() => {
                    origin = now()
                    closed = !enabled() || reports >= MAX_REPORTS
                })
                let crumbs: Crumb[] = []
                let tapTimer: Timer | null = null
                let stallTimer: Timer | null = null
                let tapCancelled = false
                let apiOwnsSubmit = false
                let unlisten: (() => void) | null = null
                const add = (event: string, data: CrumbData = {}) => {
                    if (closed) return
                    crumbs.push({ ...data, t: Math.round(now() - origin), e: event })
                    if (crumbs.length > MAX_CRUMBS) crumbs = crumbs.slice(-MAX_CRUMBS)
                }
                const clearTap = () => {
                    if (tapTimer !== null) safe(() => clearTimer(tapTimer))
                    tapTimer = null
                    const stop = unlisten
                    unlisten = null
                    if (stop) safe(stop)
                }
                const finish = () => {
                    clearTap()
                    if (stallTimer !== null) safe(() => clearTimer(stallTimer))
                    stallTimer = null
                    closed = true
                    active.delete(trace)
                    if (pressed?.trace === trace) pressed = null
                }
                const send = (kind: ReplySubmitReportKind) => {
                    safe(() => {
                        if (closed || !enabled() || reports >= MAX_REPORTS) return
                        add('report', { kind })
                        reports++
                        report(kind, crumbs.slice())
                    })
                    finish()
                }
                const trace: ReplySubmitTrace = {
                    crumb(event, data) {
                        safe(() => add(event, data))
                    },
                    startWork() {
                        safe(() => {
                            if (closed || stallTimer !== null) return
                            add('post_answer_begin')
                            stallTimer = setTimer(() => send('stalled'), STALL_WINDOW_MS)
                        })
                    },
                    apiBegin() {
                        apiOwnsSubmit = true
                        safe(() => add('api_begin'))
                    },
                    submitEnd(outcome, data) {
                        safe(() => add('submit_end', { outcome, ...data }))
                        if (outcome === 'handed_off' && !apiOwnsSubmit) finish()
                        if (outcome === 'blocked') {
                            if (data?.reason) send('blocked')
                            else finish()
                        }
                    },
                    requestSent(data) {
                        safe(() => add('request_sent', data))
                        finish()
                    },
                    finish,
                }
                if (!closed) active.add(trace)
                return {
                    trace,
                    press(snapshot: CrumbData) {
                        safe(() => {
                            if (closed) return
                            add('press', snapshot)
                            if (tapTimer !== null) return
                            const fromPage = (event: string, data?: CrumbData) => {
                                safe(() => {
                                    if (event === 'pointercancel' || event === 'touchcancel')
                                        tapCancelled = true
                                    add(event, data)
                                })
                            }
                            // A broken listener must not prevent the timer from being armed.
                            safe(() => {
                                unlisten = listen?.(fromPage) ?? null
                            })
                            tapTimer = setTimer(() => {
                                if (tapCancelled) finish()
                                else send('tap_lost')
                            }, TAP_WINDOW_MS)
                        })
                    },
                    begin(data?: CrumbData) {
                        clearTap()
                        trace.crumb('submit_begin', data)
                    },
                }
            }
            return {
                press(snapshot: CrumbData = {}) {
                    // Touch handlers can run even on a disabled button.
                    if (snapshot.can_submit === false) {
                        safe(() => remember('press_disabled', snapshot))
                        return
                    }
                    safe(() => remember('press'))
                    pressed ||= attempt()
                    pressed.press(snapshot)
                },
                submitBegin(data?: CrumbData): ReplySubmitTrace {
                    safe(() => {
                        lastSubmitAt = now()
                        remember('submit_begin', data)
                    })
                    const submission = pressed || attempt()
                    pressed = null
                    submission.begin(data)
                    // No stall timer while the writer is answering a confirmation.
                    return submission.trace
                },
                crumb(event: string, data?: CrumbData) {
                    safe(() => remember(event, data))
                    active.forEach(trace => trace.crumb(event, data))
                },
                // A touch landed inside the composer (pointerdown, capture phase).
                touch(snapshot: CrumbData = {}) {
                    safe(() => {
                        lastTouchAt = now()
                        recent = [{ ...snapshot, t: 0, e: 'touch' }]
                    })
                },
                // The composer is closing or folding.
                closed(data: CrumbData & { has_draft?: boolean } = {}) {
                    safe(() => {
                        const at = now()
                        const touched =
                            lastTouchAt !== null && at - lastTouchAt <= CLOSE_AFTER_TOUCH_MS
                        const submitted =
                            lastSubmitAt !== null && at - lastSubmitAt <= RECENT_SUBMIT_MS
                        remember('closed', data)
                        // A watched press reports this close itself, as tap_lost.
                        if (!data.has_draft || !touched || submitted || pressed) return
                        if (!enabled() || reports >= MAX_REPORTS) return
                        const crumbs = recent.slice()
                        crumbs.push({
                            t: Math.round(at - (lastTouchAt as number)),
                            e: 'report',
                            kind: 'closed_unsent',
                        })
                        lastTouchAt = null
                        recent = []
                        reports++
                        report('closed_unsent', crumbs)
                    })
                },
            }
        },
    }
}

// Share network progress only among attempts for the SAME in-flight request.
// A retry joining after fetch was invoked must not claim it never started.
export function createReplyRequestDiagnostics() {
    const observers = new Set<ReplySubmitTrace>()
    let last: { event: string; data?: CrumbData } | null = null
    let sent: CrumbData | null = null
    let settled = false
    return {
        attach(trace?: ReplySubmitTrace | null) {
            if (!trace) return
            if (sent) trace.requestSent(sent)
            else if (settled) trace.finish()
            else {
                observers.add(trace)
                if (last) trace.crumb(last.event, last.data)
            }
        },
        crumb(event: string, data?: CrumbData) {
            last = { event, data }
            observers.forEach(trace => trace.crumb(event, data))
        },
        requestSent(data: CrumbData = {}) {
            sent = data
            observers.forEach(trace => trace.requestSent(data))
            observers.clear()
        },
        finish() {
            settled = true
            observers.forEach(trace => trace.finish())
            observers.clear()
        },
    }
}
export type ReplyRequestDiagnostics = ReturnType<typeof createReplyRequestDiagnostics>

const TARGET_TEST_IDS = new Set([
    'title-composer',
    'reply-composer',
    'title-post-button',
    'reply-post-button',
    'reply-open-full-editor-button',
])

/** Only fixed control labels: arbitrary DOM ids/test ids can contain post/user ids. */
export function describeTarget(target: EventTarget | null | undefined): string {
    try {
        const el = target as Element | null
        if (!el || typeof el.tagName !== 'string') return 'none'
        const tag = el.tagName.toLowerCase()
        const safeTag = /^(button|input|textarea|div|span|svg|path|a|p|body)$/.test(tag)
            ? tag
            : 'element'
        const testId = el.closest?.('[data-testid]')?.getAttribute('data-testid')
        return safeTag + (testId && TARGET_TEST_IDS.has(testId) ? '[' + testId + ']' : '')
    } catch {
        return 'none'
    }
}
